// hooks/useBuyNow.ts
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import {
  confirmPaymentService,
  verifyPaymentService,
} from "@/services/payment";
import { openRazorpay } from "@/services/razorpay";
import {
  OfferType,
  RewardAmountResponse,
} from "@/lib/payment"; import { calculatePayment } from "@/lib/utils/payment";
import {
  getRewardAmount,
  getChildSpecialOffer,
} from "@/services/offers";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import storage from "@/lib/storage";
import { getUserIdFromToken } from "@/lib/jwt";
import { getUserDetailsService } from "@/services/auth";
import { getScreensPermissions } from "@/services/masters";
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { setPermissions } from "@/lib/store/slices/PermissionsSlice";
import { updateChild } from "@/services/parent";

type PlanItem = {
  planName?: string;
  amount?: number;
  duration?: string;
  range?: string;
};

type PaymentData = {
  subscriptionCode?: string;
  childIds?: number[];
  items: PlanItem[];
  totalAmount?: number;
  child?: any;
  shouldUpdateChildOnPay?: boolean;
  Range:any
};

type RazorpayOrder = {
  order_id: string;
  amount: number;
  currency: string;
  key: string;
}

type UseBuyNowProps = {
  username?: string;
  onSuccess?: () => void;
  onRefresh?: () => Promise<void>;
};

type SpecialOfferResponse = {
  special_offer_amount: number;
};

export default function useBuyNow({
  username,
  onSuccess,onRefresh
}: UseBuyNowProps = {}) {
  const [openBuyNowModal, setOpenBuyNowModal] =
    useState(false);

  const [paymentData, setPaymentData] =
    useState<PaymentData | null>(null);

  const [isProcessingPayment, setIsProcessingPayment] =
    useState(false);

  const [rewardBalance, setRewardBalance] =
    useState(0);
  const [offers, setOffers] = useState<OfferType[]>([]);
  const [selectedOfferAmount, setSelectedOfferAmount] = useState(0);
  const [usedRewardAmount, setUsedRewardAmount] = useState(0);
  const [showSavingsModal, setShowSavingsModal] = useState(false);
  const [specialOffers, setSpecialOffers] = useState<any[]>([]);
  const [selectedSpecialOfferAmount, setSelectedSpecialOfferAmount] = useState(0);

const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user);
  // Open modal from any file
 const openBuyNow = async (data: any) => {
  console.log(data,'data')
  const safeData: PaymentData = {
    subscriptionCode:
      data?.subscriptionCode ||
      data?.Subscription_Code ||
      "",

    childIds:
      Array.isArray(data?.childIds)
        ? data.childIds
        : [],

    totalAmount:
      Number(
        data?.totalAmount || 0
      ),

    items:
      Array.isArray(data?.items)
        ? data.items
        : [],
Range:data.Range,
    child:
      data?.child || null,
      shouldUpdateChildOnPay:
    data?.shouldUpdateChildOnPay ?? false,
  };

  setRewardBalance(0);
  setOffers([]);
  setSpecialOffers([]);
  setSelectedOfferAmount(0);
  setUsedRewardAmount(0);
  setSelectedSpecialOfferAmount(0);

  setPaymentData(
    safeData
  );

  try {
    const rewardsRes =
      await getRewardAmount(
        username
      );

    const rewardData =
      rewardsRes?.data as RewardAmountResponse;

    setRewardBalance(
      Number(
        rewardData?.Reward_Balance || 0
      )
    );

    const normalOffers =
      Array.isArray(
        rewardData?.Offers
      )
        ? rewardData.Offers
        : [];

    setOffers(
      normalOffers
    );

    if (
      safeData.childIds.length >
      0
    ) {
      const specialRes =
        await getChildSpecialOffer(
          safeData.childIds
        );

      const specialData =
        specialRes?.data as SpecialOfferResponse;

      const specialAmount =
        Number(
          specialData?.special_offer_amount ?? 0
        );

      const formattedSpecialOffers =
        specialAmount > 0
          ? [
              {
                title:
                  "Special Offer",

                Discount_Amount:
                  specialAmount,

                description:
                  `Save ₹${specialAmount}`,
              },
            ]
          : [];

      setSpecialOffers(
        formattedSpecialOffers
      );
    } else {
      setSpecialOffers([]);
    }
  } catch (error) {
    console.error(
      "BuyNow Error",
      error
    );

    setRewardBalance(0);
    setOffers([]);
    setSpecialOffers([]);

    toast.error(
      "Failed to load offers"
    );
  }

  setOpenBuyNowModal(
    true
  );
};


  const fetchUser = useCallback(async () => {
    try {
      const authToken = storage.getAuthToken();
      const userId = getUserIdFromToken(authToken!);
      if (userId) {
        const [{ data }, permRes] = await Promise.all([
          getUserDetailsService(Number(userId)),
          getScreensPermissions(),
        ]);
        dispatch(updateUserData(data as any));
        if (permRes.status === 200 && permRes.data) {
          const perms = (permRes.data as any).permissions ?? {};
          storage.setPermissions(perms);
          dispatch(setPermissions(perms));
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  }, [dispatch]);

  const closeBuyNow = async () => {
    setOpenBuyNowModal(false);

    setPaymentData(null);
    setShowSavingsModal(false);
    await onRefresh?.();
  };

const basePaymentCalculation = calculatePayment({
  totalAmount: paymentData?.totalAmount || 0,
  offerAmount: selectedOfferAmount,
  rewardAmount: usedRewardAmount,
  specialOfferAmount: selectedSpecialOfferAmount,
});

const childDiscount = Number(
  paymentData?.child?.child_discount ?? 0
);

const childDiscountAmount = Number(
  paymentData?.child?.child_discount_amount ?? 0
);

const childOriginalAmount = Number(
  paymentData?.child?.child_original_amount ??
  (
    Number(paymentData?.totalAmount || 0) +
    childDiscountAmount
  )
);

const paymentCalculation = {
  ...basePaymentCalculation,

  childOriginalAmount,

  childDiscount,

  childDiscountAmount,
};

const handleBuyNow = async () => {
  if (!paymentData) return;

  try {
    const finalAmount = Number(
      paymentCalculation?.finalAmount || 0
    );
console.log(paymentData,'paymentdata')
    if (finalAmount <= 0) {
      toast.error("Invalid payment amount");
      return;
    }

    const childId = paymentData.childIds?.[0];

    if (!childId) {
      toast.error("Child ID is missing");
      return;
    }

    /*
     * PATCH ONLY FOR NORMAL PAY FLOW
     *
     * Edit Details flow has already patched the child
     * before opening PaymentModal.
     */
    console.log(paymentData,'pa')
  {
      const latestChild = paymentData.child || {} as any;
      console.log(latestChild.items,'latestchild')
console.log(paymentData,'paymentData',latestChild)
      const latestRange = paymentData
        ? {
            ...paymentData.Range,
            Final_Amount: finalAmount,
          }
        : null;

        const childDiscount = Number(
        paymentCalculation?.childDiscount || 0
      );

      const childDiscountAmount = Number(
        paymentCalculation?.childDiscountAmount || 0
      );

      const updatePayload = {
        Range:latestRange,
        child_discount: childDiscount,
        child_discount_amount: childDiscountAmount,
      };
console.log(latestRange,'latestRange')
      console.log(
        "PATCH BEFORE PAYMENT:",
        {
          childId,
          updatePayload,
        }
      );

      const updateResponse = await updateChild(
        childId,
        updatePayload
      );

      if (
        updateResponse.status !== 200 &&
        updateResponse.status !== 201
      ) {
        toast.error(
          "Unable to update child payment details"
        );
        return;
      }
    }

    /*
     * CREATE RAZORPAY ORDER
     */
    const payload = {
      subscription_code:
        paymentData.subscriptionCode,

      child_ids:
        paymentData.childIds,

      amount:
        finalAmount,

      offer_amount:
        selectedOfferAmount,

      special_offer_amount:
        selectedSpecialOfferAmount,

      reward_amount:
        usedRewardAmount,
    };

    console.log(
      "CONFIRM PAYMENT PAYLOAD:",
      payload
    );

    const res =
      await confirmPaymentService(payload);

    const responseData = res as any;

    const data =
      responseData?.errors ||
      responseData?.data?.errors ||
      responseData?.data?.error ||
      {};

    if (
      data?.error ||
      data?.non_field_errors
    ) {
      const errorMsg = Array.isArray(
        data.error
      )
        ? data.error[0]
        : data.error;

      const nonFieldError =
        Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors;

      toast.error(
        errorMsg ||
        nonFieldError ||
        "Order creation failed"
      );

      return;
    }

    if (
      paymentCalculation.totalSavings > 0
    ) {
      setShowSavingsModal(true);

      setTimeout(() => {
        setShowSavingsModal(false);

        openRazorpay({
          order:
            res.data as RazorpayOrder,

          user: {
            name:
              username || "User",
          },

          onSuccess:
            handlePaymentSuccess,

          onFailure:
            handlePaymentFailure,
        });
      }, 2000);

      return;
    }

    openRazorpay({
      order:
        res.data as RazorpayOrder,

      user: {
        name:
          username || "User",
      },

      onSuccess:
        handlePaymentSuccess,

      onFailure:
        handlePaymentFailure,
    });

  } catch (error: any) {
    console.error(
      "PAYMENT ERROR:",
      error
    );

    const errorMsg =
      error?.response?.data?.error?.[0] ||
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong";

    toast.error(errorMsg);
  }
};

  const handlePaymentFailure = (error: any) => {
    console.error(error);
    toast.error("Payment failed ❌");
  };

 const handlePaymentSuccess = async (response: any) => {
  setIsProcessingPayment(true);

  try {
    const payload = {
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_order_id: response.razorpay_order_id,
      razorpay_signature: response.razorpay_signature,
    };

    const res = await verifyPaymentService(payload);

    if (res.status === 200) {
      toast.success("Payment Success ✅");

      // Refresh user details and update Redux
      await fetchUser();

      closeBuyNow();

      onSuccess?.();
    } else {
      toast.error("Verification failed");
    }
  } catch (error) {
    console.error(error);
    toast.error("Something went wrong");
  } finally {
    await onRefresh?.();
    setIsProcessingPayment(false);
  }
};

  return {
  openBuyNowModal,
  paymentData,
  isProcessingPayment,
  openBuyNow,
  closeBuyNow,
  handleBuyNow,
  rewardBalance,

  offers,
  specialOffers,

  paymentCalculation,
  showSavingsModal,
  selectedOfferAmount,
  setSelectedOfferAmount,
  usedRewardAmount,
  setUsedRewardAmount,
  setSelectedSpecialOfferAmount,
  selectedSpecialOfferAmount,
};
}